import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { z } from 'zod';
import { generateDeviceFingerprint } from '@/lib/deviceFingerprint';
import { Languages, Image, Lock, Zap } from 'lucide-react';
import FeatureCard from '@/components/FeatureCard';
import Footer from '@/components/Footer';
import TopNav from '@/components/TopNav';
import { useLanguage } from '@/i18n/LanguageContext';

const signUpSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  username: z.string().min(3, 'Username must be at least 3 characters').max(50),
  password: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
});

const signInSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

const Auth = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [signUpData, setSignUpData] = useState({ email: '', username: '', password: '' });
  const [signInData, setSignInData] = useState({ email: '', password: '' });
  const [resetEmail, setResetEmail] = useState('');
  const { t } = useLanguage();

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      signUpSchema.parse(signUpData);

      // Step 1: Sign up the user first (fingerprint check happens server-side after auth)
      const redirectUrl = `${window.location.origin}/`;
      const { error } = await supabase.auth.signUp({
        email: signUpData.email,
        password: signUpData.password,
        options: { emailRedirectTo: redirectUrl, data: { username: signUpData.username } },
      });

      if (error) {
        if (error.message.includes('already registered')) toast.error(t('auth.alreadyRegistered'));
        else toast.error(error.message);
        return;
      }

      // Step 2: Generate client fingerprint and complete registration server-side
      const fingerprint = await generateDeviceFingerprint();
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: regResult, error: regError } = await supabase.functions.invoke('register-user-complete', {
          body: { fingerprint_hash: fingerprint },
        });

        if (regError) {
          console.error('Registration completion error:', regError);
        } else if (regResult?.abuse_detected) {
          toast.warning(t('auth.deviceTrialUsed'));
        }
      }

      toast.success(t('auth.accountCreated'));
      navigate('/');
    } catch (error) {
      if (error instanceof z.ZodError) toast.error(error.errors[0].message);
      else toast.error('An error occurred during sign up');
    } finally { setLoading(false); }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      signInSchema.parse(signInData);
      const { error } = await supabase.auth.signInWithPassword({ email: signInData.email, password: signInData.password });
      if (error) {
        if (error.message.includes('Invalid login credentials')) toast.error(t('auth.invalidCredentials'));
        else toast.error(error.message);
        return;
      }
      toast.success(t('auth.signedIn'));
      navigate('/');
    } catch (error) {
      if (error instanceof z.ZodError) toast.error(error.errors[0].message);
      else toast.error('An error occurred during sign in');
    } finally { setLoading(false); }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await supabase.auth.resetPasswordForEmail(resetEmail, { redirectTo: `${window.location.origin}/auth/reset-password` });
      toast.success(t('auth.resetSent'));
      setForgotPassword(false);
    } catch { toast.error('An error occurred'); } finally { setLoading(false); }
  };

  if (forgotPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-secondary/20 to-accent/20">
        <TopNav />
        <div className="flex items-center justify-center p-4" style={{ minHeight: 'calc(100vh - 4rem)' }}>
          <Card className="w-full max-w-md shadow-lg">
            <CardHeader>
              <CardTitle>{t('auth.resetPassword')}</CardTitle>
              <CardDescription>{t('auth.resetDescription')}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordReset} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-email">{t('auth.email')}</Label>
                  <Input id="reset-email" type="email" placeholder="your-email@example.com" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} required />
                </div>
                <Button type="submit" className="w-full" disabled={loading}>{loading ? t('auth.sending') : t('auth.sendResetLink')}</Button>
                <Button type="button" variant="ghost" className="w-full" onClick={() => setForgotPassword(false)}>{t('auth.backToSignIn')}</Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-secondary/10 to-accent/10">
      <TopNav />
      <main>
        <section className="relative overflow-hidden py-8 px-4">
          <div className="absolute inset-0 bg-[var(--hero-gradient)] opacity-30" />
          <div className="container mx-auto max-w-4xl text-center relative z-10">
            <p className="text-lg md:text-xl font-bold text-primary mb-2 animate-in fade-in slide-in-from-bottom-4 duration-1000">{t('auth.translatorAiApp')}</p>
            <h1 className="text-5xl md:text-6xl font-bold mb-3 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent animate-in fade-in slide-in-from-bottom-4 duration-1000">{t('auth.breakDownBarriers')}</h1>
            <p className="text-lg md:text-xl text-foreground/80 mb-4 max-w-2xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-150">{t('auth.authSubtitle')}</p>
          </div>
        </section>

        <section className="container mx-auto px-4 pb-6 max-w-6xl">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <FeatureCard icon={Languages} title={t('auth.multipleLanguages')} description={t('auth.multipleLanguagesDesc')} />
            <FeatureCard icon={Image} title={t('auth.imageTranslation')} description={t('auth.imageTranslationDesc')} />
            <FeatureCard icon={Lock} title={t('auth.securePrivate')} description={t('auth.securePrivateDesc')} />
            <FeatureCard icon={Zap} title={t('auth.lightningFast')} description={t('auth.lightningFastDesc')} />
          </div>
        </section>

        <section className="container mx-auto px-4 pb-4 max-w-2xl text-center animate-in fade-in slide-in-from-bottom-4 duration-1000">
          <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent mb-2">{t('auth.signUpFreeTrial')}</h2>
          <p className="text-muted-foreground italic mb-2">{t('auth.noCreditCard')}</p>
          <div className="flex justify-center"><div className="w-12 h-1 rounded-full bg-gradient-to-r from-primary to-accent" /></div>
        </section>

        <section className="container mx-auto px-4 pb-8 max-w-md">
          <Card className="shadow-lg">
            <CardHeader className="text-center">
              <CardTitle>{t('auth.welcomeToTranslator')}</CardTitle>
              <CardDescription>{t('auth.getStartedFreeTrial')}</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="signin" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="signin">{t('auth.signIn')}</TabsTrigger>
                  <TabsTrigger value="signup">{t('auth.signUp')}</TabsTrigger>
                </TabsList>
                <TabsContent value="signin">
                  <form onSubmit={handleSignIn} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="signin-email">{t('auth.email')}</Label>
                      <Input id="signin-email" type="email" placeholder="your@email.com" value={signInData.email} onChange={(e) => setSignInData({ ...signInData, email: e.target.value })} required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signin-password">{t('auth.password')}</Label>
                      <Input id="signin-password" type="password" value={signInData.password} onChange={(e) => setSignInData({ ...signInData, password: e.target.value })} required />
                    </div>
                    <Button type="button" variant="link" className="px-0 h-auto" onClick={() => setForgotPassword(true)}>{t('auth.forgotPassword')}</Button>
                    <Button type="submit" className="w-full" disabled={loading}>{loading ? t('auth.signingIn') : t('auth.signIn')}</Button>
                  </form>
                </TabsContent>
                <TabsContent value="signup">
                  <form onSubmit={handleSignUp} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="signup-email">{t('auth.email')}</Label>
                      <Input id="signup-email" type="email" placeholder="your@email.com" value={signUpData.email} onChange={(e) => setSignUpData({ ...signUpData, email: e.target.value })} required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-username">{t('auth.username')}</Label>
                      <Input id="signup-username" type="text" placeholder={t('auth.chooseUsername')} value={signUpData.username} onChange={(e) => setSignUpData({ ...signUpData, username: e.target.value })} required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-password">{t('auth.password')}</Label>
                      <Input id="signup-password" type="password" value={signUpData.password} onChange={(e) => setSignUpData({ ...signUpData, password: e.target.value })} required />
                      <p className="text-xs text-muted-foreground">{t('auth.passwordHint')}</p>
                    </div>
                    <Button type="submit" className="w-full" disabled={loading}>{loading ? t('auth.creatingAccount') : t('auth.signUp')}</Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Auth;
